import type { Ref } from 'vue';
import type { ValidData as AddressBookAddressDialogFormValidData } from '@/components/dialogForms/AddressBookAddressDialogForm.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { ref } from 'vue';
import { isCancel } from 'axios';

import { useDialogStore } from '@/stores/useDialogStore';
import {
  fetchDeleteAddressBookAddressesById,
  fetchGetAddressBookAddressbooks,
  fetchPostAddressBookAddresses,
  fetchPutAddressBookAddressesById,
  fetchPutAddressBookAddressesByIdMoveTo,
  fetchPutAddressBookCustomer,
} from '@/helpers/fetchers';

export type AddressBookItem =
  openapi.AddressBookAddressbooksListContentOpenapi[number];
export type AddressBookAddressItem = AddressBookItem['addresses'][number];

// Shaped for the create (POST) body and reused for edit; warehouse/line3/city
// aren't sent - the PUT patches, so unprovided fields keep their stored value
export function buildAddressBody(
  formData: AddressBookAddressDialogFormValidData,
  addressbookId: number,
): openapi.AddressBookAddressesPostBodyOpenapi {
  return {
    suburb: formData.suburb.id,
    addressbook: addressbookId,
    name: formData.name ?? '',
    line1: formData.line1,
    line2: formData.line2,
    note_name: formData.noteName,
    note_phone: formData.notePhone,
    note_text: formData.noteText,
  };
}

export interface AddressBookViewDataComposable {
  books: Ref<AddressBookItem[]>;
  isFetchingBooks: Ref<boolean>;
  booksError: Ref<boolean>;
  customBooks: Ref<AddressBookItem[]>;
  isFetchingCustomBooks: Ref<boolean>;
  customBooksError: Ref<boolean>;
  isReordering: Ref<boolean>;
  fetchOwnBooks: () => Promise<void>;
  fetchCustomBooks: (params: {
    customer?: string;
    username?: string;
  }) => Promise<void>;
  clearCustomBooks: () => void;
  createAddress: (
    data: openapi.AddressBookAddressesPostBodyOpenapi,
  ) => Promise<void>;
  updateAddress: (
    id: number,
    addressbookId: number,
    data: openapi.AddressBookAddressesPutBodyOpenapi,
  ) => Promise<void>;
  deleteAddress: (id: number, addressbookId: number) => Promise<void>;
  reorderAddress: (
    id: number,
    addressbookId: number,
    newIndex: number,
  ) => Promise<void>;
  setDefaultAddress: (
    id: number,
    addressbookId: number,
    customerCode: string,
  ) => Promise<void>;
  searchCustomerCode: Ref<string | null>;
  searchUsername: Ref<string>;
  hasCustomSearch: Ref<boolean>;
  handleCustomerCodeUpdate: (value: string | null | undefined) => void;
  handleUsernameInput: (value: string | null | undefined) => void;
  handleSearchClick: () => void;
  openAddAddressDialog: (book: AddressBookItem) => void;
  openEditAddressDialog: (
    book: AddressBookItem,
    address: AddressBookAddressItem,
  ) => void;
  openDeleteAddressDialog: (
    book: AddressBookItem,
    address: AddressBookAddressItem,
  ) => void;
  handleReorder: (book: AddressBookItem, id: number, newIndex: number) => void;
  handleSetDefault: (
    book: AddressBookItem,
    address: AddressBookAddressItem,
  ) => void;
}

export default function useAddressBookViewData(): AddressBookViewDataComposable {
  const dialogStore = useDialogStore();

  const books = ref<AddressBookItem[]>([]);
  const isFetchingBooks = ref(false);
  const booksError = ref(false);
  const customBooks = ref<AddressBookItem[]>([]);
  const isFetchingCustomBooks = ref(false);
  const customBooksError = ref(false);

  let booksController: AbortController | undefined;
  let customBooksController: AbortController | undefined;

  async function fetchOwnBooks(): Promise<void> {
    booksController?.abort();
    const controller = new AbortController();
    booksController = controller;
    isFetchingBooks.value = true;
    booksError.value = false;
    try {
      books.value = await fetchGetAddressBookAddressbooks(
        {},
        { signal: controller.signal },
      );
    } catch (err) {
      // A failed load must not read as "no addresses" - flag it so the view
      // renders an error state instead of the empty placeholder.
      if (!isCancel(err)) {
        books.value = [];
        booksError.value = true;
      }
    } finally {
      if (booksController === controller) isFetchingBooks.value = false;
    }
  }

  async function fetchCustomBooks(params: {
    customer?: string;
    username?: string;
  }): Promise<void> {
    customBooksController?.abort();
    const controller = new AbortController();
    customBooksController = controller;
    isFetchingCustomBooks.value = true;
    customBooksError.value = false;
    try {
      customBooks.value = await fetchGetAddressBookAddressbooks(params, {
        signal: controller.signal,
      });
    } catch (err) {
      if (!isCancel(err)) {
        customBooks.value = [];
        customBooksError.value = true;
      }
    } finally {
      if (customBooksController === controller) {
        isFetchingCustomBooks.value = false;
      }
    }
  }

  function clearCustomBooks(): void {
    customBooks.value = [];
  }

  function findBookAddresses(
    addressbookId: number,
  ): AddressBookAddressItem[] | undefined {
    const book =
      books.value.find((b) => b.id === addressbookId) ??
      customBooks.value.find((b) => b.id === addressbookId);
    return book?.addresses;
  }

  async function createAddress(
    data: openapi.AddressBookAddressesPostBodyOpenapi,
  ): Promise<void> {
    const created = await fetchPostAddressBookAddresses(data);
    findBookAddresses(data.addressbook)?.push(created);
  }

  async function updateAddress(
    id: number,
    addressbookId: number,
    data: openapi.AddressBookAddressesPutBodyOpenapi,
  ): Promise<void> {
    const updated = await fetchPutAddressBookAddressesById(id, data);
    const addresses = findBookAddresses(addressbookId);
    const index = addresses?.findIndex((a) => a.id === id) ?? -1;
    if (index !== -1) addresses?.splice(index, 1, updated);
  }

  async function deleteAddress(
    id: number,
    addressbookId: number,
  ): Promise<void> {
    await fetchDeleteAddressBookAddressesById(id);
    const addresses = findBookAddresses(addressbookId);
    const index = addresses?.findIndex((a) => a.id === id) ?? -1;
    if (index !== -1) addresses?.splice(index, 1);
  }

  const isReordering = ref(false);

  async function reorderAddress(
    id: number,
    addressbookId: number,
    newIndex: number,
  ): Promise<void> {
    // Serialise reorders: a second drop landing mid-flight would capture a
    // fromIndex against an array the first move has already shifted, so the
    // rollback would restore to the wrong slot. The drag handle is disabled
    // while this is true (see AddressBookTable), so a dropped drag is a no-op.
    if (isReordering.value) return;
    const addresses = findBookAddresses(addressbookId);
    if (!addresses) return;
    const fromIndex = addresses.findIndex((a) => a.id === id);
    if (fromIndex === -1 || fromIndex === newIndex) return;

    // Move optimistically so the row stays where it was dropped, then roll back
    // if the server rejects it (the fetcher already surfaces the error).
    isReordering.value = true;
    const [moved] = addresses.splice(fromIndex, 1);
    addresses.splice(newIndex, 0, moved);
    try {
      await fetchPutAddressBookAddressesByIdMoveTo(id, newIndex + 1);
    } catch (err) {
      const currentIndex = addresses.findIndex((a) => a.id === id);
      if (currentIndex !== -1) {
        const [reverted] = addresses.splice(currentIndex, 1);
        addresses.splice(fromIndex, 0, reverted);
      }
      throw err;
    } finally {
      isReordering.value = false;
    }
  }

  async function setDefaultAddress(
    id: number,
    addressbookId: number,
    customerCode: string,
  ): Promise<void> {
    await fetchPutAddressBookCustomer(id, customerCode);
    // is_default is per customer/book, so only touch the book that owns the
    // address - flipping every book would desync the others from the server.
    const addresses = findBookAddresses(addressbookId);
    if (!addresses) return;
    for (const address of addresses) {
      address.is_default = address.id === id;
    }
  }

  const searchCustomerCode = ref<string | null>(null);
  const searchUsername = ref('');
  const hasCustomSearch = ref(false);

  function handleCustomerCodeUpdate(value: string | null | undefined): void {
    searchCustomerCode.value = value ?? null;
    if (value) searchUsername.value = '';
  }

  function handleUsernameInput(value: string | null | undefined): void {
    searchUsername.value = value ?? '';
    if (value) searchCustomerCode.value = null;
  }

  function handleSearchClick(): void {
    if (!searchCustomerCode.value && !searchUsername.value) {
      hasCustomSearch.value = false;
      clearCustomBooks();
      return;
    }
    hasCustomSearch.value = true;
    void fetchCustomBooks({
      customer: searchCustomerCode.value ?? undefined,
      username: searchUsername.value || undefined,
    });
  }

  function openAddAddressDialog(book: AddressBookItem): void {
    dialogStore.open('addressBookAddress', {
      isEdit: false,
      confirm: async (formData) => {
        await createAddress(buildAddressBody(formData, book.id));
      },
    });
  }

  function openEditAddressDialog(
    book: AddressBookItem,
    address: AddressBookAddressItem,
  ): void {
    dialogStore.open('addressBookAddress', {
      isEdit: true,
      initialData: {
        name: address.name,
        line1: address.line1 ?? undefined,
        line2: address.line2 ?? undefined,
        suburb: address.suburb,
        noteName: address.note_name,
        notePhone: address.note_phone,
        noteText: address.note_text,
      },
      confirm: async (formData) => {
        await updateAddress(
          address.id,
          book.id,
          buildAddressBody(formData, book.id),
        );
      },
    });
  }

  function openDeleteAddressDialog(
    book: AddressBookItem,
    address: AddressBookAddressItem,
  ): void {
    dialogStore.open('addressBookDeleteAddress', {
      address,
      confirm: async () => {
        await deleteAddress(address.id, book.id);
      },
    });
  }

  // The fetchers already surface errors (and reorderAddress rolls back its
  // optimistic move), so these swallow the rejection to keep it off the console.
  function handleReorder(
    book: AddressBookItem,
    id: number,
    newIndex: number,
  ): void {
    void reorderAddress(id, book.id, newIndex).catch(() => {
      // already surfaced by the fetcher; optimistic move rolled back
    });
  }

  function handleSetDefault(
    book: AddressBookItem,
    address: AddressBookAddressItem,
  ): void {
    // Setting a default is a per-customer action, so it only applies to a book
    // that has a customer_code (the toggle is hidden otherwise in the view).
    if (!book.customer_code) return;
    void setDefaultAddress(address.id, book.id, book.customer_code).catch(
      () => {
        // already surfaced by the fetcher
      },
    );
  }

  return {
    books,
    isFetchingBooks,
    booksError,
    customBooks,
    isFetchingCustomBooks,
    customBooksError,
    isReordering,
    fetchOwnBooks,
    fetchCustomBooks,
    clearCustomBooks,
    createAddress,
    updateAddress,
    deleteAddress,
    reorderAddress,
    setDefaultAddress,
    searchCustomerCode,
    searchUsername,
    hasCustomSearch,
    handleCustomerCodeUpdate,
    handleUsernameInput,
    handleSearchClick,
    openAddAddressDialog,
    openEditAddressDialog,
    openDeleteAddressDialog,
    handleReorder,
    handleSetDefault,
  };
}
